import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { Button, ConfirmDialog, Pill } from '@/shared/components';
import { toApiError, useApi, useApiProblem } from '@/shared/api';
import { onboardingErrorText } from './onboardingErrors';
import { countAlarming, sortPendingOnboardings } from './pendingOnboardings';
import { PendingOnboardingRow } from './PendingOnboardingRow';

/**
 * Hängende Zustellungen des Gastzugangs mit dem Grund, an dem sie hängen. Der Endpunkt dazu gab
 * es seit A3; sichtbar war er nirgends — gefunden wurde der Live-Befund nur, weil jemand acht
 * Minuten lang auf eine Buchungsseite geschaut hat.
 */
export function PendingOnboardingsPanel() {
  const api = useApi();
  const qc = useQueryClient();
  // Der Ladezustand gehört an die Zeile, nicht an die Mutation — sonst dreht ein Klick jeden
  // Knopf der Liste. Gleiche Ausprägung wie im Mail-Ausgang.
  const [busyBookingId, setBusyBookingId] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<{
    bookingId: string;
    reference: string;
    via: string | null;
  } | null>(null);
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['system', 'pendingOnboardings'],
    queryFn: () => api.system.pendingOnboardings(),
  });

  const reIssue = useMutation({
    mutationFn: (bookingId: string) => api.system.onboardingLinkReIssue(bookingId),
    onSuccess: () => {
      toast.success('Die Gäste-E-Mail liegt im Ausgang.');
      void qc.invalidateQueries({ queryKey: ['system', 'pendingOnboardings'] });
      void qc.invalidateQueries({ queryKey: ['system', 'onboardingLinks'] });
    },
    onError: async (error) => {
      toast.error(
        onboardingErrorText(
          await toApiError(error),
          'Der Zugang konnte nicht neu ausgestellt werden.',
        ),
      );
    },
    onSettled: () => setBusyBookingId(null),
  });

  const problem = useApiProblem(error);
  const retryDelivery = useMutation({
    mutationFn: (deliveryId: string) => api.system.onboardingDeliveryRetry(deliveryId),
    onSuccess: () => {
      toast.success('Zustellung erneut eingereiht');
      void qc.invalidateQueries({ queryKey: ['system', 'pendingOnboardings'] });
    },
    onError: async (error) => {
      toast.error(
        onboardingErrorText(
          await toApiError(error),
          'Die Zustellung ließ sich nicht neu einreihen.',
        ),
      );
    },
    onSettled: () => setBusyBookingId(null),
  });

  if (isLoading) return null;
  const reason = problem
    ? problem.status === 0
      ? 'Keine Verbindung.'
      : `(Code ${problem.title})`
    : null;
  // Ein Ladefehler darf nicht wie „nichts hängt“ aussehen.
  if (isError || !data) {
    return (
      <div
        role="alert"
        className="border-line bg-card mb-4 flex flex-wrap items-center gap-3 rounded-lg border p-4 text-sm"
      >
        <span className="min-w-0 flex-1">
          Hängende Zustellungen konnten nicht geladen werden.{reason ? ` ${reason}` : ''}
        </span>
        <Button variant="secondary" onClick={() => void refetch()}>
          Erneut laden
        </Button>
      </div>
    );
  }
  if (data.length === 0) {
    return (
      <p className="text-ink-soft mb-4 flex items-center gap-2 text-sm">
        <Check size={14} strokeWidth={2} className="text-sage" aria-hidden />
        Jede anstehende Buchung hat ihren Gastzugang.
      </p>
    );
  }

  const rows = sortPendingOnboardings(data);
  const alarming = countAlarming(rows);

  return (
    <section className="border-line bg-card shadow-card mb-6 rounded-lg border p-5">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <h2 className="text-ink font-serif text-lg">Zustellung hängt</h2>
        <Pill variant={alarming > 0 ? 'stop' : 'default'} size="sm">
          {rows.length} {rows.length === 1 ? 'Buchung' : 'Buchungen'}
        </Pill>
        {alarming > 0 && (
          <span className="text-sealwax text-sm">
            {alarming} davon {alarming === 1 ? 'braucht' : 'brauchen'} eine Hand.
          </span>
        )}
      </div>
      <ul className="divide-line/60 divide-y">
        {rows.map((row) => (
          <PendingOnboardingRow
            key={row.bookingId}
            row={row}
            busy={busyBookingId === row.bookingId}
            onReIssue={() =>
              setConfirming({
                bookingId: row.bookingId,
                reference: row.bookingReference,
                // Der Weg der letzten Zustellung: E-Mail für manuelle, Nachricht im Portal für Kanalbuchungen.
                via: !row.delivery
                  ? null
                  : row.delivery.transport === 'Smtp'
                    ? 'per E-Mail'
                    : 'als Nachricht im Buchungsportal',
              })
            }
            onRetry={() => {
              if (!row.delivery) return;
              setBusyBookingId(row.bookingId);
              retryDelivery.mutate(row.delivery.deliveryId);
            }}
          />
        ))}
      </ul>
      <ConfirmDialog
        open={confirming !== null}
        onOpenChange={(open) => !open && setConfirming(null)}
        title="Zugang neu ausstellen?"
        description={`${confirming?.reference ?? 'Die Buchung'} bekommt einen neuen Check-in-Link ${confirming?.via ?? 'per E-Mail oder als Nachricht im Buchungsportal'}. Bisherige Links aus Mail und Chat gelten weiter.`}
        confirmLabel="Neu ausstellen"
        variant="brand"
        onConfirm={() => {
          if (!confirming) return;
          setBusyBookingId(confirming.bookingId);
          reIssue.mutate(confirming.bookingId);
          setConfirming(null);
        }}
      />
    </section>
  );
}
